/**
 * @module Experience/repdash
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
// tslint:disable:max-file-line-count
// Core
import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// import { SETTINGS } from '@environment/settings';
import { ActivatedRoute } from '@angular/router';

// Services
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { RemoveSuspensionService } from '@experience/landing/common/services/remove-suspension.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { RestoreService } from '@support/shared/services/restore.service';

// Interfaces
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { TmoButton } from '@support/shared/widgets/interfaces/button.interface';
import { ChangeLineCartResponse } from '@support/api/proxies/responses/api-2033-cart.interface';
import { ManageBlackListResponse } from '@support/api/proxies/responses/api-2045.interface';
import {
  Line,
  CustomerAccountLineDeviceDetails,
} from '@support/api/proxies/responses/api-2022.interface';
import { LineSummary } from '@support/api/proxies/responses/api-2006.interface';
import { APIError } from '@support/api/common/error.interface';

// Constants
import { LINE_LANDING_CONSTANTS } from '@experience/landing/common/components/line-landing/line-landing.constants';

/**
 * Component to create modal to restore services of a line
 * @export
 * @class RestoreServiceModalComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'tmo-restore-service-modal',
  templateUrl: './restore-service-modal.component.html',
  styleUrls: ['../../../../../../style-sheets/components/html-spinner.component.scss'],
})
export class RestoreServiceModalComponent implements OnInit, OnDestroy {
  /**
   * Input variable to hold AEM content
   */
  @Input() public configurableData: ConfigData;
  /**
   * Input variable to hold isImeiValid value
   */
  @Input() public isImeiValid: boolean;
  /**
   * Variable to hold reference of Restore Service pop up
   */
  public restoreServiceModal: DynamicModal;
  /**
   * Reference to hold Accept Tmo button
   */
  public acceptButton: TmoButton;
  /**
   * Variable to hold formatted AEM text to be displayed in Html
   */
  public unblockServiceText: string;
  /**
   * Variable to hold current Customer details from 2007 API
   */
  public lineMsisdn: string;
  /**
   * Variable to hold Line name
   */
  public lineName: string;
  /**
   * Flag to handle isManageBlackListSuccess
   */
  public isManageBlackListSuccess: boolean;
  /**
   * Flag to handle isManageBlackListSuccess
   */
  public isRestoreServiceSuccess: boolean;
  /**
   * Stores the value of selected Status
   */
  public selectedStatus: string;
  /**
   * to unregister subscription
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Event Emitter to capture the link from where modal was opened
   */
  public triggerEvent: EventEmitter<Object>;
  /**
   * to check if device associated
   */
  public isDeviceAssociated: boolean;
  /**
   * to display error message
   */
  public restoreServiceErrorMessage: APIError[];
  /**
   * to set focus on modal body
   * @type {ElementRef}
   */
  @ViewChild('onModalBodyFocus', { static: true }) public onModalBodyFocus: ElementRef;
  /**
   * Boolean to check whether API 2045 is called or not.
   */
  public isPostManageBlackListCalled: boolean;
  /**
   * boolean variable to set if its web channel
   */
  // public isWebChannel: boolean;
  /**
   * Object to show error on line landing page.
   */
  public lineLandingError: Subject<APIError []>;
  /**
   * Object to show error on line landing page.
   */
  public lineLandingError$: Observable<APIError []>;
  /**
   * Stores error message
   */
  public errorMessage: string;
  /**
   * Show spinner untill API data loads
   */
  public showSpinner: boolean;
  /**
   * Stores whether line is suspended or not
   */
  public isSuspended: boolean;

  /**
   * Stores error message for suspended line
   */
  public isSuspendedErrorMessage: APIError[];

  constructor(
    public activatedRoute: ActivatedRoute,
    private commonUtilService: CommonUtilService,
    private removeSuspensionService: RemoveSuspensionService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private objectService: ObjectService,
    private restoreService: RestoreService
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.selectedStatus = '';
    this.lineName = '';
    this.isManageBlackListSuccess = false;
    this.isRestoreServiceSuccess = false;
    this.isDeviceAssociated = false;
    this.isPostManageBlackListCalled = false;
    this.lineLandingError = new Subject();
    this.lineLandingError$ = this.lineLandingError.asObservable();
    this.errorMessage = '';
    this.isSuspended = true;
  }

  /**
   * Component Lifecycle hook to initialize component
   * @returns void
   */
  public ngOnInit(): void {
    // this.isWebChannel = SETTINGS.CHANNEL.toLowerCase() === 'web';
    this.restoreServiceModal = {
      disableDismiss: true,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'restore-service-modal',
    };
    this.acceptButton = {
      id: 'acceptButton',
      type: '',
      name: this.configurableData.agreeAndSubmit,
      class: 'btn btn-primary',
    };
    this.isSuspendedErrorMessage = [
      {
        userMessage: this.objectService.get(this.configurableData, 'linesNotSuspended'),
      },
    ];
    const unblockService = this.objectService.get(this.configurableData, 'unblockService');
    this.unblockServiceText = this.commonUtilService.messageFormatter(unblockService, [
      LINE_LANDING_CONSTANTS.CUSTOMER_CARE_NUMBER,
    ]);
    this.lineMsisdn = this.stateStoreUtilsService.getMsisdn();
    this.lineName = this.objectService.get(
      this.stateStoreUtilsService.getAccountLinesInfo(),
      'customer.nickName'
    );
    this.lineLandingError$
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((error: APIError[]) => {
        if (
          this.isRestoreServiceSuccess &&
          this.isPostManageBlackListCalled &&
          !this.isManageBlackListSuccess
        ) {
          this.restoreService.setErrorMessage(this.errorMessage);
        } else if (
          (!this.isRestoreServiceSuccess && !this.isPostManageBlackListCalled) ||
          (!this.isRestoreServiceSuccess &&
            this.isPostManageBlackListCalled &&
            this.isManageBlackListSuccess)
        ) {
          this.restoreServiceErrorMessage = [
            {
              userMessage: this.objectService.get(
                this.configurableData,
                'restoreServiceErrorMessage'
              ),
            }, ...error
          ];
        } else {
          this.restoreServiceErrorMessage = [
            {
              userMessage: this.objectService.get(this.configurableData, 'tryAgainErrorMessage'),
            }, ...error
          ];
        }
      });
  }

  /**
   * Component lifecycle hook to destroy component
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Method to Open Restore Service Pop up
   * @returns void
   */
  public openRestoreServiceModal(
    event?: EventEmitter<object>,
    deviceDetailsLineInformation?: Line
  ): void {
    this.initializeModalData(deviceDetailsLineInformation);
    this.restoreServiceModal.toggleModalEmitter.emit({ open: true, close: false });
    this.triggerEvent = event;
    const timeInterval: number = 0;
    setTimeout(() => {
      this.onModalBodyFocus.nativeElement.focus();
    }, timeInterval);
  }
  /**
   * Method to initialize restore-modal data
   * @param deviceDetailsLineInformation: Line
   * @returns void
   */
  public initializeModalData(deviceDetailsLineInformation?: Line): void {
    this.selectedStatus = '';
    this.isManageBlackListSuccess = false;
    this.isRestoreServiceSuccess = false;
    this.isPostManageBlackListCalled = false;
    this.restoreServiceErrorMessage = null;
    if (this.objectService.get(deviceDetailsLineInformation, 'imei')) {
      this.isDeviceAssociated = true;
    }
  }

  /**
   * Method to close Restore Service Popup
   * @returns void
   */
  public closeRestoreServiceModal(): void {
    this.restoreServiceModal.toggleModalEmitter.emit({ open: false, close: true });
    this.selectedStatus = '';
    const target = 'target';
    this.triggerEvent[target].focus();
  }
  /**
   * Method to set the value to block or unblock status
   * @params event: string
   * @returns void
   */
  public selectToBlockUnblock(event: string): void {
    this.selectedStatus = event;
  }
  /**
   * Method to check whether Accept button will be enabled or not.
   * @returns boolean
   */
  public isDisabled(): boolean {
    const isSelectedStatus = this.commonUtilService.isNotEmpty(this.selectedStatus);
    const disableCTA = !(
      (!this.isManageBlackListSuccess && isSelectedStatus) ||
      (this.isManageBlackListSuccess && !isSelectedStatus) ||
      !this.isDeviceAssociated
    );
    return disableCTA || !this.isSuspended;
  }

  /**
   * onLineSelection Method is to used to check drop-down value selected data.
   * @params selectedLine: LineSummary
   * @returns void
   */
  public onLineSelection(selectedLine: LineSummary): void {
    this.showSpinner = true;
    this.selectedStatus = '';
    this.errorMessage = '';
    this.restoreServiceErrorMessage = null;
    this.isSuspended =
      selectedLine.lineStatus === LINE_LANDING_CONSTANTS.SUSPENDED ||
      (selectedLine.lineStatus === LINE_LANDING_CONSTANTS.SUSPENDED &&
        selectedLine.lineSubStatus === LINE_LANDING_CONSTANTS.ELSTS);
    this.removeSuspensionService.getDeviceDetails(selectedLine.msisdn).subscribe(
      (data: CustomerAccountLineDeviceDetails) => {
        this.showSpinner = false;
        this.isImeiValid =
          this.objectService.get(data, 'devicedetails.lines.0.isImeiValid') || false;
        this.initializeModalData(data.devicedetails.lines[0]);
      },
      () => {
        this.showSpinner = false;
        this.isDeviceAssociated = false;
      }
    );
  }

  /**
   * Method to remove suspension from service and Device. It will call 316 API to remove suspension
   * @returns void
   */
  public restoreServices(): void {
    const blockImei = this.selectedStatus === LINE_LANDING_CONSTANTS.BLOCK_MY_IMEI ? true : false;
    if (
      this.selectedStatus === LINE_LANDING_CONSTANTS.BLOCK_MY_IMEI ||
      this.selectedStatus === LINE_LANDING_CONSTANTS.UNBLOCK_MY_IMEI
    ) {
      this.blockUnblockImei(blockImei);
    }
    this.getServiceRestored();
  }
  /**
   * calls API 2045 to block and unblock Imei.
   * @param blockImei
   * @returns void
   */
  private blockUnblockImei(blockImei?: boolean): void {
    this.isPostManageBlackListCalled = true;
    this.removeSuspensionService
      .postManageBlackList(blockImei)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response: ManageBlackListResponse) => {
          this.isManageBlackListSuccess = true;
        },
        () => {
          this.isManageBlackListSuccess = false;
          // this.errorMessage = this.isWebChannel ? this.commonUtilService.messageFormatter(
          //   this.objectService.get(this.configurableData, 'blockUnblockErrorMessageForWeb'),
          //   blockImei ? ['blocking', 'block'] : ['unblocking', 'unblock'])
          //   : this.commonUtilService.messageFormatter(
          //     this.objectService.get(this.configurableData, 'blockUnblockErrorMessage'),
          //     blockImei ? ['blocking'] : ['unblocking']);
          this.errorMessage = this.commonUtilService.messageFormatter(
            this.objectService.get(this.configurableData, 'blockUnblockErrorMessageForWeb'),
            blockImei ? ['blocking', 'block'] : ['unblocking', 'unblock']
          );
        }
      );
  }
  /**
   * call API 2033 to restore the service
   * @returns void
   */
  private getServiceRestored(): void {
    this.removeSuspensionService
      .restoreServices()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response: ChangeLineCartResponse) => {
          this.isRestoreServiceSuccess = true;
          const data =
            !this.isPostManageBlackListCalled ||
            (this.isPostManageBlackListCalled && this.isManageBlackListSuccess);
          this.restoreService.setBlockedUnblocked(response.orderId, data);
          this.lineLandingError.next(null);
          this.closeRestoreServiceModal();
        },
        (error: APIError[]) => {
          this.isRestoreServiceSuccess = false;
          this.selectedStatus = '';
          this.lineLandingError.next(error);
        }
      );
  }
}
